import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRightOnRectangleIcon, BellIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { logout } from '../../store/slices/authSlice'
import DevLogin from '../DevLogin'
import api from '../../services/api'

const PAGE_TITLES: Record<string, string> = {
  '/platform-admin': 'Platform Overview',
  '/platform-admin/organizations': 'Organizations',
  '/platform-admin/users': 'All Users',
  '/platform-admin/ai-usage': 'AI Usage',
  '/platform-admin/audit-log': 'Audit Log',
  '/platform-admin/prompts': 'Prompt Library',
  '/platform-admin/session-security': 'Session Security',
  '/tenant-admin': 'Org Dashboard',
  '/tenant-admin/users': 'User Management',
  '/tenant-admin/settings': 'Organization Settings',
  '/tenant-admin/prompts': 'AI Prompts',
  '/tenant-admin/audit-log': 'Audit Log',
  '/admin/mapkit': 'Apple Maps',
}

const TYPE_ICONS: Record<string, string> = {
  use_case: '📋',
  brand: '🏢',
  template: '✨',
  deal_room: '🤝',
}

interface SearchResult {
  type: string
  id: string
  title: string
  subtitle: string
  url: string
}

function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(query)}`)
        setResults(data.results || [])
        setOpen(true)
      } catch { setResults([]) }
      finally { setLoading(false) }
    }, 280)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [query])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur() }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  return (
    <div className="relative">
      <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 transition-all ${open ? 'border-violet-400 bg-white shadow-sm w-72' : 'border-gray-200 bg-gray-50 w-52'}`}>
        <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Search… (⌘K)"
          className="flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none"
        />
        {loading && <div className="w-3 h-3 rounded-full border border-violet-400 border-t-transparent animate-spin flex-shrink-0" />}
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
          {results.map(r => (
            <button
              key={`${r.type}-${r.id}`}
              onMouseDown={() => { navigate(r.url); setOpen(false); setQuery('') }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-violet-50 transition-colors text-left"
            >
              <span className="text-base">{TYPE_ICONS[r.type] || '📄'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                {r.subtitle && <p className="text-xs text-gray-400 truncate">{r.subtitle}</p>}
              </div>
              <span className="text-[10px] font-medium text-gray-400 capitalize flex-shrink-0">{r.type.replace('_', ' ')}</span>
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 2 && !loading && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-50 px-4 py-3">
          <p className="text-sm text-gray-400">No results for "{query}"</p>
        </div>
      )}
    </div>
  )
}

export default function Header() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const pageTitle = PAGE_TITLES[location.pathname] || ''

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
  }

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-3">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          {pageTitle && (
            <p className="text-sm font-medium text-gray-500">{pageTitle}</p>
          )}
        </div>

        <GlobalSearch />

        <div className="flex items-center gap-2">
          <DevLogin />
          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <BellIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1.5" />
            Logout
          </button>
        </div>
      </div>
    </header>
  )
}
